/**
 * Pure helpers for social lead activity on the client overview.
 *
 * Partition conversations (DMs) from engagement (comments/reactions) and
 * resolve multi-platform social accounts for reply composers.
 */

export type SocialActivityKind = 'conversations' | 'engagement';

/** Prototype A inbox tabs: Messenger | IG Direct | Posts. */
export type SocialInboxTab = 'messenger' | 'instagram' | 'posts';

export type SocialAccountRef = {
  platform: string;
  external_user_id: string;
};

export type PostEngagementGroup = {
  key: string;
  title: string;
  items: SocialInteractionLike[];
};

export type SocialInteractionLike = {
  type: string;
  text?: string;
  attachment_type?: string;
  reaction_type?: string;
  property_id?: string;
  publication_id?: string;
  permalink?: string;
  captured_at: string;
  platform?: string;
};

const CONVERSATION_TYPES = new Set(['message', 'outbound_message', 'postback']);
const ENGAGEMENT_TYPES = new Set(['comment', 'reaction', 'lead_ad']);
const SOCIAL_CONTACT_TYPES = new Set(['facebook', 'instagram', 'facebook_id', 'instagram_id']);

/**
 * Returns true when the interaction belongs in the Conversas tab.
 */
export function isConversationType(type: string): boolean {
  return CONVERSATION_TYPES.has(type);
}

const LAST_SEEN_KEY_PREFIX = 'housien:social-last-seen:';

/**
 * Records "now" as the last time this viewer saw a client's Social updates.
 * Per-viewer/per-browser only (localStorage) -- there is no cross-device
 * read receipt, same tradeoff as this app's other lightweight UI state.
 *
 * Without a `platform`, this is the general "opened/looked at the widget"
 * mark that drives the launcher bubble and client-list dot. With one, it
 * additionally marks that specific channel seen -- used to clear a single
 * inbox tab's dot without touching the others (see `hasUnseenSocialUpdate`).
 */
export function markSocialSeen(clientId: string, platform?: string): void {
  try {
    const key = platform
      ? `${LAST_SEEN_KEY_PREFIX}${clientId}:${platform}`
      : `${LAST_SEEN_KEY_PREFIX}${clientId}`;
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    /* localStorage unavailable (private mode, blocked, etc.) -- badge just won't clear */
  }
}

/**
 * Get last social seen at.
 */
function getLastSocialSeenAt(clientId: string, platform?: string): string {
  try {
    if (platform) {
      const perPlatform = localStorage.getItem(`${LAST_SEEN_KEY_PREFIX}${clientId}:${platform}`);
      if (perPlatform) return perPlatform;
    }
    return localStorage.getItem(`${LAST_SEEN_KEY_PREFIX}${clientId}`) ?? '';
  } catch {
    return '';
  }
}

/**
 * True when a client has at least one inbound interaction (anything but our
 * own outbound_message) captured after this viewer last saw their Social
 * updates -- drives the unseen-update dot on the launcher bubble, client
 * list, and (passing `platform`) a single inbox tab.
 *
 * A `platform` filter checks only that channel's interactions against its
 * own last-seen mark (falling back to the general one until that channel
 * has been individually viewed), so e.g. reading Messenger doesn't clear an
 * unread Instagram tab.
 */
export function hasUnseenSocialUpdate(
  clientId: string,
  interactions?: SocialInteractionLike[],
  platform?: string,
): boolean {
  if (!clientId || !interactions?.length) return false;
  const lastSeenAt = getLastSocialSeenAt(clientId, platform);
  return interactions.some((item) => isUnseenUpdate(item, lastSeenAt, platform));
}

/**
 * One interaction, measured against a last-seen mark: inbound, newer than the
 * mark, and on the channel being asked about (any channel when unfiltered).
 */
function isUnseenUpdate(
  item: SocialInteractionLike,
  lastSeenAt: string,
  platform?: string,
): boolean {
  if (item.type === 'outbound_message' || item.captured_at <= lastSeenAt) return false;
  return !platform || (item.platform ?? '') === platform;
}

/**
 * Returns true when the interaction belongs in the Engajamento tab.
 */
export function isEngagementType(type: string): boolean {
  return ENGAGEMENT_TYPES.has(type);
}

/**
 * Splits interactions into conversation vs engagement buckets.
 */
export function partitionInteractions(items: SocialInteractionLike[]): {
  conversations: SocialInteractionLike[];
  engagement: SocialInteractionLike[];
} {
  const conversations: SocialInteractionLike[] = [];
  const engagement: SocialInteractionLike[] = [];
  for (const item of items) {
    if (isConversationType(item.type)) {
      conversations.push(item);
    } else {
      engagement.push(item);
    }
  }
  return { conversations, engagement };
}

/**
 * Filters interactions by platform when a platform filter is set.
 */
export function filterByPlatform(
  items: SocialInteractionLike[],
  platform: string | 'all',
  fallbackPlatform?: string,
): SocialInteractionLike[] {
  if (platform === 'all') return items;
  return items.filter((item) => (item.platform || fallbackPlatform || '') === platform);
}

/**
 * Normalizes contact type aliases (facebook_id → facebook).
 */
export function normalizeSocialPlatform(raw: string): string {
  if (raw === 'facebook_id') return 'facebook';
  if (raw === 'instagram_id') return 'instagram';
  return raw;
}

/**
 * Returns true when the platform is a supported Meta social channel.
 */
export function isMetaSocialPlatform(platform: string): boolean {
  return platform === 'facebook' || platform === 'instagram';
}

type AccountAccumulator = {
  seen: Set<string>;
  accounts: SocialAccountRef[];
};

/**
 * Pushes a social account into the accumulator when it is new and valid.
 */
export function pushSocialAccount(
  acc: AccountAccumulator,
  platform: string,
  externalUserId: string,
): void {
  const normalized = normalizeSocialPlatform(platform);
  if (!normalized || !externalUserId || !isMetaSocialPlatform(normalized)) return;
  const key = `${normalized}:${externalUserId}`;
  if (acc.seen.has(key)) return;
  acc.seen.add(key);
  acc.accounts.push({ platform: normalized, external_user_id: externalUserId });
}

/**
 * Adds declared social_accounts from lead_origin into the accumulator.
 */
function addOriginAccounts(
  acc: AccountAccumulator,
  leadOrigin?: {
    platform?: string;
    external_user_id?: string;
    social_accounts?: SocialAccountRef[];
  },
): void {
  for (const account of leadOrigin?.social_accounts ?? []) {
    pushSocialAccount(acc, account.platform, account.external_user_id);
  }
  if (leadOrigin?.platform && leadOrigin?.external_user_id) {
    pushSocialAccount(acc, leadOrigin.platform, leadOrigin.external_user_id);
  }
}

/**
 * Adds Meta contact entries into the accumulator.
 */
function addContactAccounts(
  acc: AccountAccumulator,
  contacts?: Array<{ type: string; value: string }>,
): void {
  for (const contact of contacts ?? []) {
    if (SOCIAL_CONTACT_TYPES.has(contact.type)) {
      pushSocialAccount(acc, contact.type, contact.value);
    }
  }
}

/**
 * Resolves social accounts from lead_origin, contacts, and interaction stamps.
 */
export function resolveSocialAccounts(input: {
  leadOrigin?: {
    platform?: string;
    external_user_id?: string;
    social_accounts?: SocialAccountRef[];
  };
  contacts?: Array<{ type: string; value: string }>;
  interactions?: SocialInteractionLike[];
}): SocialAccountRef[] {
  const acc: AccountAccumulator = { seen: new Set(), accounts: [] };
  addOriginAccounts(acc, input.leadOrigin);
  addContactAccounts(acc, input.contacts);
  return acc.accounts;
}

/**
 * Builds a Facebook or Instagram permalink when possible.
 */
export function buildSocialPostUrl(publicationId: string, platform?: string): string {
  if (!publicationId) return '';
  if (platform === 'instagram') {
    return `https://www.instagram.com/p/${publicationId}/`;
  }
  const parts = publicationId.split('_');
  if (parts.length >= 2) {
    return `https://www.facebook.com/permalink.php?story_fbid=${parts.slice(1).join('_')}&id=${parts[0]}`;
  }
  return `https://www.facebook.com/${publicationId}`;
}

/**
 * Formats a captured-at ISO timestamp for display.
 */
export function formatSocialCapturedAt(iso: string): string {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Maps a Prototype A inbox tab to its Meta platform, or null for Posts.
 */
export function platformForInboxTab(tab: SocialInboxTab): string | null {
  if (tab === 'messenger') return 'facebook';
  if (tab === 'instagram') return 'instagram';
  return null;
}

/**
 * Returns the first linked social account for a platform.
 */
export function findAccountForPlatform(
  accounts: SocialAccountRef[],
  platform: string,
): SocialAccountRef | undefined {
  return accounts.find((account) => account.platform === platform);
}

/**
 * Conversation (DM) messages for one channel / platform.
 */
export function conversationMessagesForPlatform(
  items: SocialInteractionLike[],
  platform: string,
  fallbackPlatform?: string,
): SocialInteractionLike[] {
  const conversations = items.filter((item) => isConversationType(item.type));
  return filterByPlatform(conversations, platform, fallbackPlatform);
}

/** Meta's standard messaging window: a Page/IG account can only send a
 * free-form reply within 24h of the user's last inbound message -- see
 * SocialMessageService._graph_error_result on the backend. */
export const MESSAGING_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * True for an inbound DM: a conversation item the client sent us, never one
 * of our own outbound replies. Only these open Meta's messaging window.
 */
function isInboundConversation(item: SocialInteractionLike): boolean {
  return isConversationType(item.type) && item.type !== 'outbound_message';
}

/**
 * Timestamp of the most recent inbound DM in a conversation thread, or
 * `undefined` if the client never sent one (only our own outbound replies,
 * or none at all). Only `message`/`postback` count -- comments/reactions
 * don't open Meta's messaging window.
 */
export function lastInboundMessageAt(messages: SocialInteractionLike[]): string | undefined {
  return messages
    .filter(isInboundConversation)
    .reduce<string | undefined>(
      (latest, item) => (latest && latest > item.captured_at ? latest : item.captured_at),
      undefined,
    );
}

/**
 * True once more than 24h have passed since `lastInboundAt` -- Graph would
 * reject a free-form reply as outside the standard messaging window.
 * `undefined` (no inbound DM yet) is treated as not-expired: that's a
 * different, not-yet-observed scenario, not a stale conversation.
 */
export function isMessagingWindowExpired(lastInboundAt: string | undefined, now = Date.now()): boolean {
  if (!lastInboundAt) return false;
  return now - new Date(lastInboundAt).getTime() > MESSAGING_WINDOW_MS;
}

/**
 * Builds a stable group key for an engagement item.
 */
function engagementGroupKey(item: SocialInteractionLike): string {
  if (item.publication_id) return `pub:${item.publication_id}`;
  if (item.property_id) return `prop:${item.property_id}`;
  return 'ungrouped';
}

/**
 * Human title for a post engagement group.
 */
function engagementGroupTitle(item: SocialInteractionLike, key: string): string {
  if (item.publication_id) return item.publication_id;
  if (item.property_id) return item.property_id;
  return key === 'ungrouped' ? 'ungrouped' : key;
}

/**
 * Groups engagement interactions by publication or property.
 */
export function groupEngagementsByPost(
  items: SocialInteractionLike[],
): PostEngagementGroup[] {
  const engagement = items.filter((item) => !isConversationType(item.type));
  const map = new Map<string, PostEngagementGroup>();
  for (const item of engagement) {
    const key = engagementGroupKey(item);
    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    map.set(key, { key, title: engagementGroupTitle(item, key), items: [item] });
  }
  return Array.from(map.values());
}
